/**
 * ReportViewer 公共组件类型定义
 * 用于 DS 报告查看器的类型约束
 */
import React from 'react';

/** 报告步骤类型 */
export type TReportStepType = 'framework' | 'report';

/** 文件项接口 */
export interface IReportFileItem {
  id: string;
  title: string;
  display_title?: string;
  version?: string;
  date_time?: string;
  [key: string]: unknown;
}

/** 报告数据接口 */
export interface IReportData {
  document_id?: string;
  version?: string;
  type?: TReportStepType;
  answer?: unknown;
  messageId?: string;
  show?: boolean;
  initialShow?: boolean;
  reportId?: string;
  /** 生成/绑定该报告时所使用的会话 ID，用于报告相关接口的 session 透传 */
  sourceSessionId?: string;
}

/** 报告更新参数 */
export interface IReportUpdateParams {
  show?: boolean;
  reportId?: string;
  [key: string]: unknown;
}

/** 步骤页签配置项 */
export interface IStepTabConfig {
  /** 中文标签 */
  label: string;
  /** 英文标签 */
  labelEn: string;
  /** 步骤值 */
  value: TReportStepType;
  /** 页签左侧图标（可选，如 AI Gallery 预览/代码） */
  icon?: React.ReactNode;
  /**
   * 是否展示「1.」序号前缀；默认 true（DS 报告 / 魔法海报）。
   * 传 false 时仅展示 icon + 文案，便于对齐设计稿 Tab。
   */
  showStepIndex?: boolean;
}

/** 步骤页签 Props */
export interface IReportStepTabsProps {
  /** 当前激活的步骤 */
  activeStep: TReportStepType;
  /** 分析报告是否禁用 */
  isReportDisabled: boolean;
  /** 步骤切换回调 */
  onStepChange: (step: TReportStepType) => void;
  /** 关闭回调 */
  onClose?: () => void;
  /** 是否显示关闭按钮 */
  showCloseButton?: boolean;
  /** 自定义步骤配置，不传则使用默认的 REPORT_STEP_TABS_CONFIG */
  stepsConfig?: IStepTabConfig[];
  /** 自定义禁用提示文案 */
  disabledTip?: string;
  /** 自定义禁用提示英文文案 */
  disabledTipEn?: string;
  /** 自定义类名（如 AI Gallery 场景可在外部追加 `dba-report-step-tabs--ai-gallery`） */
  className?: string;
}

/** 文件选择器 Props */
export interface IReportFileSelectorProps {
  /** 文件列表数据 */
  fileList: { documents?: IReportFileItem[]; };
  /** 当前选中的文件 */
  currentFile: IReportFileItem | undefined;
  /** 当前激活的文件标识（id + version） */
  activeFileKey: string;
  /** 容器宽度 */
  containerWidth: number;
  /** 当前步骤 */
  activeStep: TReportStepType;
  /**
   * 触发器标题区域 max-width（px）。
   * 传入时不再按 activeStep 使用 DS 报告的 100/520 推导，避免 AI Gallery
   * 在「预览 / 代码」两页签下文件名展示宽度不一致。
   */
  triggerTitleMaxWidthPx?: number;
  /** 文件选择回调 */
  onFileSelect: (file: IReportFileItem, closeDropdown: () => void) => void;
  /** 自定义触发器icon（可选，默认使用 agent-ds-temp-file.svg） */
  triggerIcon?: string;
  /** 自定义类名 */
  className?: string;
  /** 自定义下拉弹出层类名 */
  overlayClassName?: string;
  /** 自定义列表项图标渲染函数（可选，根据文件信息返回图标 URL） */
  itemIconRenderer?: (file: IReportFileItem) => string;
}

/** 工具栏操作项接口 */
export interface IToolbarActionItem {
  /** 操作标签（React 节点） */
  label: React.ReactNode;
  /** 对应的步骤值 */
  value: TReportStepType;
  /** 操作唯一标识 */
  key: string;
}

/** 导出文件类型配置项 */
export interface IExportFileTypeItem {
  label: string;
  value: string;
  labelEn: string;
  /** 可选图标 URL */
  icon?: string;
}

/** 工具栏 Props */
export interface IReportToolbarProps {
  /** 当前激活的步骤 */
  activeStep: TReportStepType;
  /** 流式状态 */
  streamingState: string;
  /** 是否显示复制成功提示 */
  showCopyToast?: boolean;
  /** 操作点击回调 */
  onActionClick: (action: { key: string; }) => void;
  /** 导出回调 */
  onExport: (type: string) => void;
  /** 自定义操作列表，传入时覆盖默认的 getReportToolbarActions */
  customActions?: IToolbarActionItem[];
  /** 自定义导出文件类型配置，传入时覆盖默认的 EXPORT_FILE_TYPE_CONFIG */
  customExportConfig?: IExportFileTypeItem[];
  /** 导出操作的 key 标识，默认为 EReportActions.ReportExport */
  exportActionKey?: string;
  /** 导出按钮是否禁用 */
  exportDisabled?: boolean;
  /** 流式状态下禁用的操作 key，默认为 EReportActions.ImplementGenerate */
  disabledOnStreamingKey?: string;
  /** 流式状态下禁用的操作 key 列表（优先级高于 disabledOnStreamingKey） */
  disabledOnStreamingKeys?: string[];
  /** 自定义类名 */
  className?: string;
}

/** 编辑器 Props */
export interface IReportEditorProps {
  /** 报告内容（HTML 格式） */
  content: string;
  /** 回答数据 */
  answer?: unknown;
  /** 是否加载中 */
  isLoading: boolean;
  /** Logo 容器 ref */
  logoContainerRef?: React.RefObject<HTMLDivElement>;
  /** 报告容器 ref */
  reportContainerRef?: React.RefObject<HTMLDivElement>;
  /** 自定义类名 */
  className?: string;
}

/** 弹窗类型枚举 */
export enum EReportModalType {
  Export = 'export',
  Reset = 'reset',
}

/** 弹窗配置接口 */
export interface IReportModalConfig {
  caption: string;
  bodyDesc: string;
  showCancel: boolean;
  onClick?: () => Promise<void> | void;
}

/** 弹窗 Props */
export interface IReportModalProps {
  /** 显示的弹窗类型 */
  showModal: EReportModalType | undefined;
  /** 弹窗配置 */
  modalContent: IReportModalConfig | undefined;
  /** 格式化消息函数 */
  formatMessage: (descriptor: { id: string; }) => string;
  /** 关闭回调 */
  onClose: () => void;
}

/** ReportPanel 主组件 Props */
export interface IReportPanelProps {
  /** 会话 ID */
  sessionId: string;
  /** 会话列表 */
  conversations: unknown[];
  /** 报告数据 */
  dsReport: IReportData;
  /** 流式状态 */
  streamingState: string;
  /** 报告更新回调 */
  handleUpdateDsReport: (update: IReportUpdateParams) => void;
  /** 开始新对话回调 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  startNewConversation: (...args: any[]) => void;
  /** 是否显示关闭按钮 */
  showCloseButton?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 头部鼠标按下事件 - 用于支持拖拽（仅空白区域触发） */
  onHeaderMouseDown?: (e: React.MouseEvent) => void;
  /** 窗口聚焦回调 - 用于层级切换（整个面板区域触发） */
  onFocus?: () => void;
}
